import * as React from 'react';
import { action, observable, reaction } from 'mobx';
import { store } from 'Models/Store';
import {
	Redirect,
	Route,
	RouteComponentProps,
	Switch,
} from 'react-router-dom';
import * as Pages from './Pages';
import Logout from './Components/Logout/Logout';
import Auth from './Components/Auth/Auth';
import PageNotFound from './Pages/PageNotFound';
import PageAccessDenied from './Pages/PageAccessDenied';
import Topbar from './Components/Topbar/Topbar';
import { ElementsPage } from './Pages';
import { observer } from 'mobx-react';
import { FrontendMonitor } from './FrontendMonitor';
import { FrontendAuthorisedHub } from './FrontendAuthorisedHub';
import ESignIndex from 'ESign/ESignIndex';
import Spinner from './Components/Spinner/Spinner';
import { FrontendApprove } from './FrontendApprove';

type FrontendState = {
	// State for refreshing the frontend to re-fetch everything when an organisation is impersonated
	impersonationRefresh: boolean;
};
const GraphiQlLazy = React.lazy(() => import('./Pages/Admin/Graphiql'));
// we add the observer tag here in a hacky way, to use the protected region
// the observer tag is used so we can render pages off what changes in the store

@observer
export default class Frontend extends React.Component<RouteComponentProps> {
	state: FrontendState = {
		impersonationRefresh: false,
	};

	impersonationDisposer: () => void = () => { };

	@action
	private setAppLocation = () => {
		store.appLocation = 'frontend';
	}

	public componentDidMount() {
		this.setAppLocation();

		// Subscribe to our mobx state and refresh the frontend when it changes
		this.impersonationDisposer = reaction(
			() => store.impersonatingOrganisationId,
			() => {
				this.setState((prevState: FrontendState) => ({
					refresh: !prevState.impersonationRefresh,
				}));
			}
		);
	}


	// Cleanup subscription
	componentWillUnmount() {
		this.impersonationDisposer();
	}

	public render() {
		const { match } = this.props;
		const path = match.path === '/' ? '' : match.path;

		return (
			<>
				<div className="body-container">
					<Topbar currentLocation="frontend" />
					<div className="frontend">
						<>
							<Switch>
								{/* Public routes */}
								<Route path="/login" component={Pages.LoginRoutes} />
								<Route path="/logout" component={Logout} />
								<Route path="/confirm-email" component={Pages.RegistrationConfirmPage} />
								<Route path="/reset-password-request" component={Pages.ResetPasswordRequestPage} />
								<Route path="/reset-password" component={Pages.ResetPasswordPage} />

								<Route path={`${path}/404`} component={PageNotFound} />
								<Route path={`${path}/403`} component={PageAccessDenied} />

								<Route path="/confirm-email-setup-password" component={Pages.SetPasswordPage} />
								<Route path="/change-email" component={Pages.UpdateEmailTransientPage} />
								<Route path="/integration-setup/:partner" component={Pages.IntegrationSetupPage} />

								{/* Registration Referral landing page */}
								<Route
									path="/hub/registration/r/:registrationId"
									component={Pages.OrganisationRegistrationReferralPage}
								/>
								{/* they might have a registrationID if returning to saved registration */}
								<Route
									path="/hub/registration/:registrationId"
									component={Pages.OrganisationRegistrationPage}
								/>
								<Route
									path="/hub/registration"
									component={Pages.OrganisationRegistrationPage}
								/>
								<Route
									path="/hub/referral-partner/registration/:registrationId"
									component={Pages.ReferralPartnerRegistrationPage}
								/>
								<Route
									path="/hub/referral-partner/registration"
									component={Pages.ReferralPartnerRegistrationPage}
								/>
								{/* ESign Routes */}
								<Route path="/esign" component={ESignIndex} />
								{process.env.NODE_ENV === 'development' && (
									<Route path="/elements" component={ElementsPage} />
								)}

								{process.env.NODE_ENV === 'development' && (
									<Route path={`${path}/graphiql`}>
										<React.Suspense fallback={null}>
											<GraphiQlLazy />
										</React.Suspense>
									</Route>
								)}

								<Auth {...this.props}>
									<Switch>
										{/* These routes require a login to view */}
										{/* Pages from the ui model */}

										<Redirect exact from="/" to={`${path}/hub/products`} />
										<Route path="/hub">
											<FrontendAuthorisedHub path={path} />
										</Route>
										<Route path="/approve">
											<FrontendApprove path={path} />
										</Route>
										<Route path="/monitor">
											<FrontendMonitor path={path} />
										</Route>

										<Route component={PageNotFound} />
									</Switch>
								</Auth>
							</Switch>
						</>
					</div>
				</div>
			</>
		);
	}
}
